const OWNED_BLOG_CTA_PATHS = [
  "/affiliate",
  "/chat",
  "/create",
  "/explore",
  "/generate",
  "/pricing",
  "/register",
] as const

const OWNED_BLOG_UTM_PARAMS = {
  utm_source: "blog",
  utm_medium: "owned_content",
} as const

const BLOG_REGISTER_MAGNET_PARAMS = {
  utm_source: "blog",
  utm_medium: "seo",
} as const

const BLOG_REGISTER_CAMPAIGN_SLOT_SEPARATOR = "--"

const NON_ARTICLE_BLOG_SEGMENTS = new Set(["archive"])

const PLEASUR_ORIGIN = "https://pleasur.ai"

// Apex, www, and PR preview hosts (<pr>.pleasur.ai), same as supabase/functions/_shared/cors.ts.
const PLEASUR_HOSTNAME = /^(?:[a-z0-9-]+\.)?pleasur\.ai$/

function parsePleasurUrl(href: string): URL | null {
  const url = new URL(href, PLEASUR_ORIGIN)
  return PLEASUR_HOSTNAME.test(url.hostname) ? url : null
}

function sanitizeRegisterMagnetSlot(contentSlot: string): string {
  return (
    contentSlot
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "cta"
  )
}

function registerMagnetCampaign(articleSlug: string, contentSlot: string): string {
  return `${articleSlug}${BLOG_REGISTER_CAMPAIGN_SLOT_SEPARATOR}${sanitizeRegisterMagnetSlot(contentSlot)}`
}

function setRegisterMagnetUtmParams(url: URL, campaign: string): void {
  url.search = ""
  url.searchParams.set("utm_source", BLOG_REGISTER_MAGNET_PARAMS.utm_source)
  url.searchParams.set("utm_medium", BLOG_REGISTER_MAGNET_PARAMS.utm_medium)
  url.searchParams.set("utm_campaign", campaign)
}

function isOwnedBlogCta(url: URL): boolean {
  if (url.pathname === "/" && url.searchParams.get("auth") === "register") {
    return true
  }

  return OWNED_BLOG_CTA_PATHS.some(
    (path) => url.pathname === path || url.pathname.startsWith(`${path}/`)
  )
}

function isRegisterMagnetPath(url: URL): boolean {
  return url.pathname === "/register" || (url.pathname === "/" && url.searchParams.get("auth") === "register")
}

export function getBlogArticleSlug(pathname: string): string | null {
  const match = pathname.match(/^\/blog\/([^/?#]+)/)
  if (!match?.[1]) return null

  const slug = decodeURIComponent(match[1])
  return NON_ARTICLE_BLOG_SEGMENTS.has(slug) ? null : slug
}

export function withBlogRegisterMagnetUtm(
  href: string,
  articleSlug: string,
  contentSlot = "primary_cta"
): string {
  const url = parsePleasurUrl(href)
  if (!url || !isRegisterMagnetPath(url)) return href

  url.pathname = "/register"
  url.hash = ""
  setRegisterMagnetUtmParams(url, registerMagnetCampaign(articleSlug, contentSlot))

  return `${url.pathname}${url.search}${url.hash}`
}

export function withOwnedBlogUtm(href: string, articleSlug?: string | null): string {
  if (!articleSlug) return href

  const url = parsePleasurUrl(href)
  if (!url || !isOwnedBlogCta(url)) {
    return href
  }

  if (isRegisterMagnetPath(url)) {
    return withBlogRegisterMagnetUtm(href, articleSlug)
  }

  if (url.searchParams.size === 0) {
    return `${url.pathname}${url.hash}`
  }

  url.searchParams.set("utm_source", OWNED_BLOG_UTM_PARAMS.utm_source)
  url.searchParams.set("utm_medium", OWNED_BLOG_UTM_PARAMS.utm_medium)
  url.searchParams.set("utm_campaign", articleSlug)

  return `${url.pathname}${url.search}${url.hash}`
}

export function preserveBlogSeoUtmHref(href: string): string {
  if (href.startsWith("#")) return href

  try {
    const url = parsePleasurUrl(href)
    if (!url) return href

    if (url.pathname === "/generate") {
      url.pathname = "/generate/image"
    }

    if (url.pathname === "/register" && url.searchParams.get("utm_source") === "blog") {
      const campaign = url.searchParams.get("utm_campaign")?.trim() || "blog"
      const legacyContentSlot = url.searchParams.get("utm_content")

      setRegisterMagnetUtmParams(
        url,
        legacyContentSlot
          ? registerMagnetCampaign(campaign, legacyContentSlot)
          : campaign
      )

      return `${url.pathname}${url.search}${url.hash}`
    }

    url.searchParams.delete("utm_source")
    url.searchParams.delete("utm_medium")
    url.searchParams.delete("utm_campaign")
    url.searchParams.delete("utm_content")

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}
